import type { FC } from 'react';
import { Box, Chip, Divider, Skeleton, Typography } from '@packages/shared';
import { Fragment } from 'react';
import type { ListFilter, Filter } from '../../api/rest/types';

export type FilterDetailsLoaderProps = {
  /** The filter for which the loader should be provided */
  filter: Filter;
};

/**
 * Provides a loading state for the filter details
 * */
export const FilterDetailsLoader: FC<FilterDetailsLoaderProps> = ({ filter }) => {
  const typeMappings = {
    list: (
      <Box data-testid="list-loader">
        {(filter as ListFilter).isSearchable && (
          <Skeleton
            data-testid="searchbox-skeleton"
            width="100%"
            height={90}
            variant="rectangular"
          />
        )}
        {Array.from({ length: 3 }, (_, index) => (
          <Fragment key={`listitem-${index}`}>
            <Box
              sx={{
                display: 'flex',
                position: 'relative',
                gap: 2,
                alignItems: 'center',
                width: '100%',
                padding: 1,
              }}
            >
              <Skeleton width={40} variant="rectangular" />
              <Typography variant="body1" width="inherit">
                <Skeleton width="100%" />
              </Typography>
              <Chip label={<Skeleton width={20} />} />
            </Box>
            <Divider />
          </Fragment>
        ))}
      </Box>
    ),
    patch: (
      <Box data-testid="patch-loader" px={2}>
        <Typography>
          <Skeleton height={40} />
        </Typography>
        <Box sx={{ display: 'flex', position: 'relative', gap: 2, my: 1 }}>
          <Skeleton height={60} width={60} variant="rectangular" />
          <Skeleton height={60} width={60} variant="rectangular" />
          <Skeleton height={60} width={60} variant="rectangular" />
          <Skeleton height={60} width={60} variant="rectangular" />
        </Box>
      </Box>
    ),
    slider: (
      <Box data-testid="slider-loader">
        <Typography>
          <Skeleton height={40} />
        </Typography>
        <Box
          sx={{
            display: 'flex',
            position: 'relative',
            gap: 2,
            marginTop: 2.5,
            justifyContent: 'space-between',
          }}
        >
          <Chip label={<Skeleton width={20} />} />
          <Chip label={<Skeleton width={20} />} />
        </Box>
        <Box sx={{ mx: 3, position: 'relative' }}>
          <Skeleton
            height={filter.type === 'slider' && filter?.slider?.quantities ? 130 : 45}
            variant="rectangular"
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            position: 'relative',
            marginTop: 1.75,
          }}
        >
          <Typography>
            <Skeleton width={100} />
          </Typography>
          <Typography>
            <Skeleton width={100} />
          </Typography>
        </Box>
      </Box>
    ),
  };
  return typeMappings[filter.type];
};
